/**
 * DAY-SCOPE-PRIMITIVE-AND-ADOPTION-V1 -- THE ONE DAY-SCOPE MODEL.
 *
 * The pure half of the day-scope primitive: the clinic-day arithmetic, the
 * past / today / future verdict, the URL state contract, the scoped-query
 * contract, and the stale-response gate. No React, no fetching, no ambient
 * clock -- every function is a total function of its arguments (the repo
 * testability idiom: pure helpers here, a view that renders from props, a
 * container that wires state).
 *
 * WHY THIS MODULE EXISTS (founder ruling 2026-08-04): for day-driven roles
 * Home IS the operating surface for the SELECTED clinic day -- default today,
 * previous / next, arbitrary date, one-click Today, refresh-safe deep link,
 * honest past / present / future truth. That behaviour was previously LOCAL
 * markup + local state on the Appointments planning calendar and existed
 * nowhere else. One control grammar product-wide needs ONE owner.
 *
 * BOUNDARY LAW (A4) -- NO SECOND DAY DEFINITION. The clinic day is the
 * EXISTING IST business day (Asia/Kolkata, a fixed +05:30 offset with no
 * DST). The four arithmetic functions below are the APPOINTMENT-BOOK-V1
 * implementations MOVED here unchanged -- `appointment-calendar-view.ts`
 * now re-exports them from this module, so there is exactly one
 * implementation and every existing import and pin keeps resolving. The
 * SERVER's `istDayWindow` (lib/domain/appointment/booking-rules.ts) stays
 * THE owner of the half-open [start, next-start) instant bounds; this module
 * never computes instant bounds, so a day switch can never disagree with the
 * read it drives.
 *
 * CONFIGURABILITY SEAM (recorded, not invented here): `clinics.timezone` is
 * the recorded seam for a non-IST market (api/routes/appointments-read.ts
 * istBusinessDateOf). When that seam is built it lands in the ONE existing
 * IST owner and this module inherits it -- never as a second timezone
 * implementation.
 *
 * WHAT THIS MODULE DELIBERATELY DOES NOT OWN
 *  - instant bounds for the read: the server's istDayWindow (above);
 *  - status labels / tones: the StatusChip canon;
 *  - the row contract of any adopting surface: each surface keeps its own.
 *
 * SINGLE-DAY V1 (A8): range semantics stay owned by the Money workspace law.
 * Nothing here models a range, and adding one is a NAMED DEFERRAL, not a
 * quiet extension.
 */

/** The query-string key the selected day travels in. ONE key product-wide. */
export const DAY_SCOPE_QUERY_KEY = 'date';

/** Where the selected day sits relative to the clinic's today. */
export type DayScopeKind = 'past' | 'today' | 'future';

const CLINIC_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * True for a well-shaped AND REAL clinic date ('YYYY-MM-DD'). A well-shaped
 * but non-existent day (2026-02-30) is rejected -- the value is a day KEY, so
 * an impossible day must never silently roll forward.
 *
 * (Moved verbatim from appointment-calendar-view.isCalendarDate, which now
 * re-exports this function.)
 */
export function isClinicDate(value: string): boolean {
  const m = CLINIC_DATE_PATTERN.exec(value.trim());
  if (m === null) return false;
  const [, y, mo, d] = m;
  const utcMs = Date.UTC(Number(y), Number(mo) - 1, Number(d));
  const back = new Date(utcMs);
  return (
    back.getUTCFullYear() === Number(y) &&
    back.getUTCMonth() === Number(mo) - 1 &&
    back.getUTCDate() === Number(d)
  );
}

/**
 * Step a clinic date by WHOLE CLINIC DAYS. UTC epoch arithmetic, so month /
 * year rollover and leap days are correct and the host machine's timezone can
 * never shift the result. IST has no DST, so one step is always exactly one
 * clinic day -- including across the March / November transitions that move a
 * US-local browser's own clock. An unparseable input is returned unchanged:
 * the stepper never invents a date.
 *
 * (Moved verbatim from appointment-calendar-view.stepCalendarDate.)
 */
export function stepClinicDate(date: string, deltaDays: number): string {
  if (!isClinicDate(date)) return date;
  const m = CLINIC_DATE_PATTERN.exec(date.trim()) as RegExpExecArray;
  const [, y, mo, d] = m;
  const stepped = new Date(
    Date.UTC(Number(y), Number(mo) - 1, Number(d)) + deltaDays * 86_400_000,
  );
  const yyyy = stepped.getUTCFullYear();
  const mm = String(stepped.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(stepped.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * The clinic's CURRENT calendar date for an injected instant -- the Today
 * control's target. Same fixed +05:30 arithmetic as the rest of the
 * frontend's IST module, so a US browser lands on the clinic's today (and a
 * browser sitting at 23:30 local on the far side of the offset does not land
 * a day early).
 *
 * (Moved verbatim from appointment-calendar-view.istCalendarToday.)
 */
export function clinicToday(now: Date = new Date()): string {
  const ist = new Date(now.getTime() + 330 * 60_000);
  const yyyy = ist.getUTCFullYear();
  const mm = String(ist.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(ist.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/** A human day label, e.g. '21 May 2026'. '' for an unparseable date so the
 *  caller can fall back to the raw key rather than print a guess.
 *  (Moved verbatim from appointment-calendar-view.calendarDateLabel.) */
export function clinicDateLabel(date: string): string {
  if (!isClinicDate(date)) return '';
  const m = CLINIC_DATE_PATTERN.exec(date.trim()) as RegExpExecArray;
  const [, y, mo, d] = m;
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];
  return `${Number(d)} ${months[Number(mo) - 1]} ${y}`;
}

/**
 * Where a selected day sits relative to the clinic's today. Zero-padded
 * 'YYYY-MM-DD' keys order lexicographically, so this is a plain string
 * comparison -- no second date parse, no local clock, no drift.
 * An unparseable selection resolves to 'today' (fail-safe: the caller has
 * already normalised through readDayParam, and no surface may render
 * past/future truth off a value it could not read).
 */
export function resolveDayScope(selected: string, today: string): DayScopeKind {
  if (!isClinicDate(selected) || !isClinicDate(today)) return 'today';
  if (selected === today) return 'today';
  return selected < today ? 'past' : 'future';
}

// --- URL state law (A3) -----------------------------------------------------

/** The resolution of the URL's day parameter. `malformed` is recorded so a
 *  surface can be honest about a bad deep link instead of silently pretending
 *  the user asked for today. */
export interface DayParamResolution {
  readonly date: string;
  readonly source: 'url' | 'default';
  readonly malformed: boolean;
}

/**
 * Read the selected day out of a URL parameter value.
 *   absent / empty  -> today   (the canonical Home URL carries no parameter)
 *   valid day       -> that day
 *   malformed       -> today, flagged (never a guessed date, never a crash;
 *                      the SERVER validates the value it is sent independently
 *                      -- api/routes/appointments-read.ts returns 400 on a
 *                      malformed `date`, so this fallback narrows, never widens)
 *
 * A deep-linked day is JUST A DAY: it carries no identity, no patient, no
 * clinical token, so the URL law "no PHI beyond the date" holds by
 * construction. Role and org fences are unchanged -- the day travels to the
 * same fenced read a normal navigation issues.
 */
export function readDayParam(
  raw: string | null | undefined,
  today: string,
): DayParamResolution {
  if (raw === null || raw === undefined || raw.trim() === '') {
    return { date: today, source: 'default', malformed: false };
  }
  const value = raw.trim();
  if (!isClinicDate(value)) {
    return { date: today, source: 'default', malformed: true };
  }
  return { date: value, source: 'url', malformed: false };
}

/**
 * The next query string for a day selection, as a plain key/value list built
 * off the CURRENT parameters (every unrelated parameter is preserved).
 *
 * Selecting TODAY removes the key: the canonical Home URL is the bare route,
 * so "one-click Today" always lands on one address rather than minting a
 * second URL for the same day. Every other day writes the key, which is what
 * makes the address refresh-safe and shareable.
 */
export function writeDayParam(
  current: ReadonlyArray<readonly [string, string]>,
  date: string,
  today: string,
): Array<[string, string]> {
  const next = current.filter(([key]) => key !== DAY_SCOPE_QUERY_KEY);
  if (isClinicDate(date) && date !== today) {
    next.push([DAY_SCOPE_QUERY_KEY, date]);
  }
  return next.map(([k, v]) => [k, v]);
}

// --- Scoped-query contract (A5) ---------------------------------------------

/**
 * The ONE scoped read descriptor every adopting surface builds: exactly one
 * clinic and exactly one day. `key` is the REQUEST IDENTITY the stale-response
 * gate below matches on.
 *
 * "One day only, never the world" is structural here: the descriptor cannot
 * express a range, so no adopting surface can accidentally widen a day switch
 * into a bulk read. The instant bounds for that day are the SERVER's
 * (istDayWindow) -- this descriptor carries the day KEY, never bounds.
 */
export interface DayScopeRequest {
  readonly date: string;
  readonly clinicId: number | null;
  readonly key: string;
}

export function dayScopeRequest(args: {
  readonly date: string;
  readonly clinicId?: number | null;
}): DayScopeRequest {
  const clinicId = args.clinicId ?? null;
  return {
    date: args.date,
    clinicId,
    key: `${clinicId === null ? 'none' : String(clinicId)}|${args.date}`,
  };
}

// --- Stale-response law (A7) ------------------------------------------------

/**
 * The request-identity gate. Rapid previous / next issues several reads; only
 * the response whose identity is still the LATEST issued one may be rendered,
 * so a slow earlier day can never paint its rows under a newer date.
 *
 * Deliberately a tiny closure rather than a hook: it is a pure state machine,
 * unit-testable without a DOM, and a container holds one in a ref for the
 * lifetime of the surface. The effect-cleanup flag each container already
 * carries covers UNMOUNT; this gate covers OVERLAP.
 */
export interface DayScopeGate {
  /** Record a newly issued request and return its key. */
  issue: (key: string) => string;
  /** True only for the most recently issued key. */
  accepts: (key: string) => boolean;
  /** The key currently allowed to render ('' before the first issue). */
  latest: () => string;
}

export function createDayScopeGate(): DayScopeGate {
  let latest = '';
  return {
    issue: (key: string): string => {
      latest = key;
      return key;
    },
    accepts: (key: string): boolean => key === latest,
    latest: (): string => latest,
  };
}

// --- Day-scope vocabulary (A2 copy law, A6 truth law) -----------------------

/**
 * The section heading for a day-scoped board. Today reads as today; every
 * other day names its date. Verb-free, four words, no explanatory sentence.
 */
export function dayScopeHeading(date: string, today: string): string {
  if (resolveDayScope(date, today) === 'today') return "Today's patient flow";
  const label = clinicDateLabel(date);
  return `${label === '' ? date : label} - patient flow`;
}

/**
 * The honest read-only line for a non-today day. Today returns null: the
 * present needs no explanation, and the copy law forbids prose where the
 * controls already speak.
 */
export function dayScopeReadOnlyCopy(scope: DayScopeKind): string | null {
  if (scope === 'past') return 'Past day - recorded truth, read only.';
  if (scope === 'future') return 'Future day - planned schedule only.';
  return null;
}

/** The registered honest empty state per scope. The 'today' arm returns the
 *  EXISTING day-board copy byte-identically, so an unadopted host and an
 *  adopted host on today print the same words. */
export function dayScopeEmptyCopy(scope: DayScopeKind): {
  readonly title: string;
  readonly body: string;
} {
  if (scope === 'past') {
    return {
      title: 'No appointments on this day.',
      body: 'Nothing was booked at this clinic for this date.',
    };
  }
  if (scope === 'future') {
    return {
      title: 'No appointments booked yet.',
      body: 'Bookings for this date appear here as they are made.',
    };
  }
  return {
    title: 'No appointments today at this clinic.',
    body: 'Booked appointments for today appear here as the day fills.',
  };
}
